import {
  DEFAULT_PROPERTY_NOTIFICATION_PREFERENCES,
  getPropertyNotificationPreferences,
  propertiesApi,
  type CreatePropertyUnitRequest,
  type CreatePropertyRequest,
  type PropertyDto,
  type PropertyNotificationPreferenceDto,
} from '../../services/api';
import { ApiError } from '../../services/core.api';
import { t, translatePropertyStatus, translatePropertyType } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { PROPERTY_ICONS, PROPERTY_STATUS_OPTIONS, escapeHtml, getEditorValue } from './property-shared';

export function extractCsvImportErrors(err: unknown): string[] {
  if (err instanceof ApiError && Array.isArray(err.details?.errors) && err.details.errors.length > 0) {
    return err.details.errors.map((entry: { rowNumber?: number; message?: string }) =>
      entry.rowNumber ? t('property.importRowError', { rowNumber: entry.rowNumber }) : t('property.importError'));
  }
  if (err instanceof Error && err.message) return [err.message];
  return [t('property.importError')];
}

export function showPropertyLimitError(
  err: unknown,
  errorBox?: HTMLElement | null,
): boolean {
  if (!(err instanceof ApiError) || err.code !== 'PROPERTY_LIMIT_EXCEEDED' || !errorBox) {
    return false;
  }

  const propertyLimit = Number(err.details?.propertyLimit ?? 0);
  const recommendedPlanKey = typeof err.details?.recommendedPlanKey === 'string' ? err.details.recommendedPlanKey : '';
  const recommendedPlanName = typeof err.details?.recommendedPlanName === 'string' ? err.details.recommendedPlanName : '';
  const hasRecommendedPlan = recommendedPlanName.trim().length > 0;
  const descriptionKey = propertyLimit === 1
    ? (hasRecommendedPlan ? 'property.limit.description.singleWithPlan' : 'property.limit.description.single')
    : (hasRecommendedPlan ? 'property.limit.description.multiWithPlan' : 'property.limit.description.multi');
  const upgradeHref = recommendedPlanKey
    ? `/pricing/?recommendedPlan=${encodeURIComponent(recommendedPlanKey)}`
    : '/pricing/';

  errorBox.innerHTML = `
    <div class="property-limit-feedback-card">
      <div class="property-limit-feedback-copy">
        <strong class="property-limit-feedback-title">${escapeHtml(t('property.limit.title'))}</strong>
        <p class="property-limit-feedback-text">${escapeHtml(t(descriptionKey, {
          count: propertyLimit,
          plan: recommendedPlanName,
        }))}</p>
      </div>
      <a href="${escapeHtml(upgradeHref)}" class="btn btn-outline property-limit-feedback-link" data-upgrade-link>
        ${escapeHtml(t('property.limit.upgradeButton'))}
      </a>
    </div>
  `;
  errorBox.hidden = false;

  return true;
}

export function openPropertyForm(options: {
  property?: PropertyDto;
  onSaved: () => Promise<void>;
}): void {
  const property = options.property ?? null;
  const notificationPreferences = getPropertyNotificationPreferences(property);
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${property ? t('property.form.editTitle') : t('property.form.addTitle')}</h2>
        <button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button>
      </div>
      <form>
        <div class="form-group">
          <label>${t('common.address')} *</label>
          <input name="address" required value="${escapeHtml(property?.address || '')}" placeholder="${t('property.addressPlaceholder')}">
        </div>
        <div class="form-group">
          <label>${t('property.form.structureType')} *</label>
          <select name="structureType" ${property ? 'disabled' : ''}>
            <option value="Standalone" ${(property?.structureType || 'Standalone') === 'Standalone' ? 'selected' : ''}>${t('property.structure.standalone')}</option>
            <option value="Building" ${(property?.structureType || 'Standalone') === 'Building' ? 'selected' : ''}>${t('property.structure.building')}</option>
            <option value="Unit" ${(property?.structureType || 'Standalone') === 'Unit' ? 'selected' : ''}>${t('property.structure.unit')}</option>
          </select>
        </div>
        <div class="form-group">
          <label>${t('property.form.name')}</label>
          <input name="name" value="${escapeHtml(property?.name || '')}" placeholder="${t('property.form.namePlaceholder')}">
        </div>
        <div class="form-group" data-unit-label-group ${property?.structureType === 'Unit' ? '' : 'hidden'}>
          <label>${t('property.form.unitLabel')}</label>
          <input name="unitLabel" value="${escapeHtml(property?.unitLabel || '')}" placeholder="${t('property.form.unitLabelPlaceholder')}">
        </div>
        <div class="form-group" data-building-units-group ${property?.structureType === 'Building' && !property ? '' : 'hidden'}>
          <label>${t('property.form.units')}</label>
          <textarea name="unitLabels" rows="4" placeholder="${t('property.form.unitsPlaceholder')}"></textarea>
          <p class="modal-subtitle">${t('property.form.unitsHint')}</p>
        </div>
        <div class="form-group">
          <label>${t('common.type')} *</label>
          <select name="type" required>
            ${['Appartement', 'Huis', 'Studio', 'Commercieel', 'Garage', 'Ander'].map((type) =>
              `<option value="${type}" ${property?.type === type ? 'selected' : ''}>${escapeHtml(translatePropertyType(type))}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('common.status')} *</label>
          <select name="status" required>
            ${PROPERTY_STATUS_OPTIONS.map((status) =>
              `<option value="${status}" ${(property?.status || 'Vacant') === status ? 'selected' : ''}>${escapeHtml(translatePropertyStatus(status))}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('common.description')}</label>
          <textarea name="description" rows="3" placeholder="${t('property.descriptionPlaceholder')}">${escapeHtml(property?.description || '')}</textarea>
        </div>
        <div class="form-group">
          <label>${t('common.notes')}</label>
          <rc-rich-text-editor name="notes" placeholder="${escapeHtml(t('common.notesPlaceholder'))}" value="${escapeHtml(property?.notes || '')}"></rc-rich-text-editor>
        </div>
        <div class="form-group">
          <div class="property-notification-preferences-panel">
            <div class="property-notification-preferences-header">
              <label class="property-notification-preferences-label">${t('property.notifications.sectionTitle')}</label>
              <p class="modal-subtitle property-notification-preferences-subtitle">${t('property.notifications.sectionDescription')}</p>
            </div>
            <div class="property-notification-preferences">
            ${notificationPreferences.map((preference) => renderNotificationPreference(preference)).join('')}
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${property ? t('common.save') : t('common.add')}</button>
        </div>
        <div class="form-error property-limit-feedback" data-form-error hidden></div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  const syncStructureFields = () => {
    const structureType = (overlay.querySelector('[name="structureType"]') as HTMLSelectElement | null)?.value || property?.structureType || 'Standalone';
    overlay.querySelector<HTMLElement>('[data-unit-label-group]')?.toggleAttribute('hidden', structureType !== 'Unit');
    overlay.querySelector<HTMLElement>('[data-building-units-group]')?.toggleAttribute('hidden', structureType !== 'Building' || Boolean(property));
  };
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('[name="structureType"]')?.addEventListener('change', syncStructureFields);
  syncStructureFields();
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitBtn.disabled = true;
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
      errorBox.innerHTML = '';
    }

    const fd = new FormData(event.target as HTMLFormElement);
    const structureType = ((fd.get('structureType') as string) || property?.structureType || 'Standalone') as CreatePropertyRequest['structureType'];
    const data: CreatePropertyRequest = {
      address: fd.get('address') as string,
      name: (fd.get('name') as string) || undefined,
      type: fd.get('type') as string,
      structureType,
      status: fd.get('status') as CreatePropertyRequest['status'],
      unitLabel: structureType === 'Unit' ? ((fd.get('unitLabel') as string) || undefined) : undefined,
      description: (fd.get('description') as string) || undefined,
      notes: getEditorValue(overlay, 'notes'),
      notificationPreferences: readNotificationPreferences(overlay),
      units: structureType === 'Building' && !property ? parseUnitRequests((fd.get('unitLabels') as string) || '', (fd.get('type') as string)) : undefined,
    };

    try {
      if (property) {
        await propertiesApi.update(property.id, data);
      } else {
        await propertiesApi.create(data);
      }
      close();
      await options.onSaved();
    } catch (err) {
      console.error('Failed to save property:', err);
      showPropertyLimitError(err, errorBox);
      submitBtn.disabled = false;
    }
  });

  document.body.appendChild(overlay);
}

function parseUnitRequests(rawValue: string, defaultType: string): CreatePropertyUnitRequest[] | undefined {
  const units = rawValue
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((label) => ({
      label,
      type: defaultType,
      status: 'Vacant' as const,
    }));

  return units.length > 0 ? units : undefined;
}

function renderNotificationPreference(preference: PropertyNotificationPreferenceDto): string {
  return `
    <label class="property-notification-preference">
      <div class="property-notification-preference-copy">
        <span class="property-notification-preference-title">${t(`property.notifications.${preference.kind}.label`)}</span>
        <span class="property-notification-preference-hint">${t(`property.notifications.${preference.kind}.hint`)}</span>
      </div>
      <div class="property-notification-preference-controls">
        <span class="property-notification-preference-toggle">
          <input
            class="property-notification-preference-checkbox"
            type="checkbox"
            name="notification-enabled-${preference.kind}"
            ${preference.enabled ? 'checked' : ''}
            aria-label="${escapeHtml(t(`property.notifications.${preference.kind}.label`))}">
          <span class="property-notification-preference-switch" aria-hidden="true"></span>
        </span>
        <div class="property-notification-preference-leadtime">
        <input
          class="property-notification-preference-input"
          type="number"
          name="notification-value-${preference.kind}"
          min="1"
          step="1"
          value="${preference.leadTimeValue}"
          aria-label="${escapeHtml(t('property.notifications.leadTimeLabel', { label: t(`property.notifications.${preference.kind}.label`) }))}">
        <span class="property-notification-preference-unit">${t(`property.notifications.unit.${preference.leadTimeUnit}`)}</span>
        </div>
      </div>
    </label>
  `;
}

function readNotificationPreferences(root: ParentNode): CreatePropertyRequest['notificationPreferences'] {
  return DEFAULT_PROPERTY_NOTIFICATION_PREFERENCES.map((preference) => {
    const enabledInput = root.querySelector<HTMLInputElement>(`[name="notification-enabled-${preference.kind}"]`);
    const valueInput = root.querySelector<HTMLInputElement>(`[name="notification-value-${preference.kind}"]`);
    const leadTimeValue = Math.max(1, Number.parseInt(valueInput?.value || '', 10) || preference.leadTimeValue);

    return {
      kind: preference.kind,
      enabled: enabledInput?.checked ?? preference.enabled,
      leadTimeValue,
    };
  });
}

export function openPropertyImportForm(onSaved: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>${t('property.importTitle')}</h2>
          <p class="modal-subtitle">${t('property.importDescription')}</p>
        </div>
        <button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button>
      </div>
      <form>
        <div class="form-group">
          <a class="btn btn-outline" href="/assets/template.csv" download>${t('property.importTemplateLink')}</a>
        </div>
        <div class="form-group">
          <label for="property-import-csv-file">${t('property.importFileLabel')}</label>
          <input id="property-import-csv-file" name="csvFile" type="file" accept=".csv,text/csv">
        </div>
        <p class="modal-subtitle">${t('property.importHint')}</p>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${t('property.importButton')}</button>
        </div>
        <div class="form-error property-limit-feedback" data-form-error hidden></div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    const fileInput = overlay.querySelector<HTMLInputElement>('input[name="csvFile"]');
    const file = fileInput?.files?.[0];

    submitBtn.disabled = true;
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
      errorBox.innerHTML = '';
    }

    if (!file) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = t('property.importFileRequired');
      }
      submitBtn.disabled = false;
      return;
    }

    try {
      const result = await propertiesApi.importCsv({ content: await file.text() });
      close();
      await onSaved();
      toastService.success(t('property.importSuccess', {
        propertyCount: result.createdPropertyCount,
        tenantCount: result.createdTenantCount,
      }));
    } catch (err) {
      if (!showPropertyLimitError(err, errorBox) && errorBox) {
        errorBox.hidden = false;
        errorBox.innerHTML = extractCsvImportErrors(err).map((message) => `<div>${escapeHtml(message)}</div>`).join('');
      }
      submitBtn.disabled = false;
    }
  });

  document.body.appendChild(overlay);
}
